// ============================================
// AUTO CLOSURE NOTIFICATION COMPONENT
// Affiche les rappels de fermeture de caisse
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Clock, AlertTriangle, Bell, Volume2, CheckCircle, Moon } from 'lucide-react';
import { Button } from './ui/button';

// Sound for alerts
const ALERT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

const AutoClosureNotification = () => {
    const { api, user } = useAuth();
    const [notification, setNotification] = useState(null);
    const [openRegisters, setOpenRegisters] = useState([]);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [settings, setSettings] = useState(null);
    const audioRef = useRef(null);
    const checkIntervalRef = useRef(null);
    const wsRef = useRef(null);
    
    // Initialize audio
    useEffect(() => {
        audioRef.current = new Audio(ALERT_SOUND_URL);
        audioRef.current.volume = 1.0;
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

    // Play alert sound
    const playAlertSound = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(err => {
                console.warn('Could not play alert sound:', err);
                // Try Web Audio API fallback
                try {
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    const oscillator = audioContext.createOscillator();
                    const gainNode = audioContext.createGain();
                    oscillator.connect(gainNode);
                    gainNode.connect(audioContext.destination);
                    oscillator.frequency.value = 800;
                    oscillator.type = 'sine';
                    gainNode.gain.value = 0.5;
                    oscillator.start();
                    setTimeout(() => {
                        oscillator.stop();
                        audioContext.close();
                    }, 500);
                } catch (e) {
                    console.warn('Web Audio fallback failed:', e);
                }
            });
        }
    }, []);

    // Fetch settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api().get('/auto-closure/settings');
                setSettings(response.data);
            } catch (error) {
                // Use defaults if settings not available
                setSettings({
                    timezone: 'Africa/Libreville',
                    reminder_time: '20:00',
                    warning_time: '20:25',
                    auto_close_time: '20:30',
                    enabled: true
                });
            }
        };
        
        if (user) {
            fetchSettings();
        }
    }, [api, user]);

    // Check closure status periodically
    const checkClosureStatus = useCallback(async () => {
        if (!user || !settings?.enabled) return;
        
        try {
            // Call the check-and-execute endpoint
            const response = await api().post('/auto-closure/check-and-execute');
            const data = response.data;
            
            if (data.action) {
                // An action was triggered, fetch current status
                const statusResponse = await api().get('/auto-closure/status');
                const status = statusResponse.data;
                
                setOpenRegisters(status.open_registers || []);
                
                if (data.action === 'reminder' && status.open_count > 0) {
                    setNotification({
                        type: 'reminder',
                        message: `⏰ RAPPEL: ${status.open_count} caisse(s) encore ouverte(s). Pensez à fermer les caisses avant ${settings.auto_close_time}.`,
                        count: status.open_count
                    });
                    setShowFullScreen(true);
                    playAlertSound();
                } else if (data.action === 'warning' && status.open_count > 0) {
                    setNotification({
                        type: 'warning',
                        message: `⚠️ ATTENTION: ${status.open_count} caisse(s) encore ouverte(s). Fermeture automatique dans 5 minutes!`,
                        count: status.open_count
                    });
                    setShowFullScreen(true);
                    playAlertSound();
                } else if (data.action === 'auto_close') {
                    const closedCount = data.action_result?.count || 0;
                    if (closedCount > 0) {
                        setNotification({
                            type: 'auto_closed',
                            message: `🔒 FERMETURE AUTOMATIQUE: ${closedCount} caisse(s) fermée(s) automatiquement. Validation Admin requise.`,
                            count: closedCount
                        });
                        setShowFullScreen(true);
                        playAlertSound();
                    }
                }
            }
        } catch (error) {
            console.error('Error checking closure status:', error);
        }
    }, [api, user, settings, playAlertSound]);

    // Check for midnight reset (at 00:05)
    const checkMidnightReset = useCallback(async () => {
        if (!user) return;
        
        try {
            const response = await api().post('/daily-reports/check-midnight-reset');
            const data = response.data;
            
            if (data.status === 'executed' && data.result) {
                // Midnight reset was executed
                if (data.result.anomalies_count > 0) {
                    setNotification({
                        type: 'midnight_reset',
                        message: `🌙 RESET MINUIT: ${data.result.anomalies_count} caisse(s) fermée(s) automatiquement. Nouveau jour démarré.`,
                        count: data.result.anomalies_count
                    });
                    setShowFullScreen(true);
                    playAlertSound();
                }
            }
        } catch (error) {
            // Silently fail - this is a background check
            console.debug('Midnight reset check:', error.message);
        }
    }, [api, user, playAlertSound]);

    // Setup periodic check (every minute)
    useEffect(() => {
        if (!user || !settings?.enabled) return;
        
        // Initial check
        checkClosureStatus();
        checkMidnightReset();
        
        // Check every 60 seconds
        checkIntervalRef.current = setInterval(() => {
            checkClosureStatus();
            checkMidnightReset();
        }, 60000);
        
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, [user, settings, checkClosureStatus]);

    // Listen for WebSocket notifications
    useEffect(() => {
        const handleWebSocketMessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'closure_notification') {
                    const notifData = data.data;
                    
                    // Check if current user is targeted
                    const targetIds = notifData.target_user_ids || [];
                    if (user && (targetIds.includes(user.id) || user.role === 'ADMIN')) {
                        setNotification({
                            type: notifData.notification_type,
                            message: notifData.message,
                            count: notifData.open_count
                        });
                        setOpenRegisters(notifData.open_registers?.map(name => ({ register_name: name })) || []);
                        setShowFullScreen(true);
                        
                        if (notifData.play_sound) {
                            playAlertSound();
                        }
                    }
                }
            } catch (e) {
                // Not a JSON message or not for us
            }
        };

        // Listen on window for WebSocket messages (from POSPageV2's WebSocket)
        window.addEventListener('closure_notification', handleWebSocketMessage);
        
        return () => {
            window.removeEventListener('closure_notification', handleWebSocketMessage);
        };
    }, [user, playAlertSound]);

    // Dismiss notification
    const dismissNotification = useCallback(() => {
        setShowFullScreen(false);
        setNotification(null);
    }, []);

    // Navigate to close register (for users with open registers)
    const goToCloseCash = useCallback(() => {
        dismissNotification();
        window.location.href = '/cash-closing';
    }, [dismissNotification]);

    // If no notification or not enabled, don't render
    if (!notification || !showFullScreen) return null;

    // Color scheme based on notification type
    const colors = {
        reminder: {
            bg: 'from-blue-600 to-blue-800',
            icon: 'text-blue-200',
            accent: 'bg-blue-500'
        },
        warning: {
            bg: 'from-amber-600 to-orange-800',
            icon: 'text-amber-200',
            accent: 'bg-amber-500'
        },
        auto_closed: {
            bg: 'from-red-600 to-red-800',
            icon: 'text-red-200',
            accent: 'bg-red-500'
        },
        midnight_reset: {
            bg: 'from-indigo-600 to-purple-800',
            icon: 'text-indigo-200',
            accent: 'bg-indigo-500'
        }
    };

    const colorScheme = colors[notification.type] || colors.reminder;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className={`absolute inset-0 bg-gradient-to-br ${colorScheme.bg} opacity-95`}
                onClick={dismissNotification}
            />
            
            {/* Content */}
            <div className="relative z-10 max-w-2xl w-full mx-4 text-center text-white">
                {/* Close button */}
                <button
                    onClick={dismissNotification}
                    className="absolute -top-4 -right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>
                
                {/* Icon */}
                <div className={`mx-auto w-24 h-24 rounded-full ${colorScheme.accent} flex items-center justify-center mb-6 animate-pulse`}>
                    {notification.type === 'reminder' && <Bell className="w-12 h-12" />}
                    {notification.type === 'warning' && <AlertTriangle className="w-12 h-12" />}
                    {notification.type === 'auto_closed' && <Clock className="w-12 h-12" />}
                    {notification.type === 'midnight_reset' && <Moon className="w-12 h-12" />}
                </div>
                
                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold mb-4">
                    {notification.type === 'reminder' && 'Rappel de Fermeture'}
                    {notification.type === 'warning' && 'Fermeture Imminente!'}
                    {notification.type === 'auto_closed' && 'Fermeture Automatique'}
                    {notification.type === 'midnight_reset' && 'Nouveau Jour'}
                </h1>
                
                {/* Message */}
                <p className="text-xl md:text-2xl mb-6 opacity-90">
                    {notification.message}
                </p>
                
                {/* Open registers list */}
                {openRegisters.length > 0 && (
                    <div className="mb-8">
                        <p className="text-lg font-semibold mb-3">Caisses concernées:</p>
                        <div className="flex flex-wrap justify-center gap-2">
                            {openRegisters.map((reg, idx) => (
                                <span 
                                    key={idx}
                                    className="px-4 py-2 rounded-full bg-white/20 text-sm font-medium"
                                >
                                    {reg.register_name || reg}
                                    {reg.user_name && ` (${reg.user_name})`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                        onClick={goToCloseCash}
                        size="lg"
                        className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8"
                    >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Fermer ma caisse
                    </Button>
                    
                    <Button
                        onClick={dismissNotification}
                        variant="outline"
                        size="lg"
                        className="border-white/50 text-white hover:bg-white/10 px-8"
                    >
                        Plus tard
                    </Button>
                </div>
                
                {/* Sound indicator */}
                <div className="mt-8 flex items-center justify-center gap-2 text-sm opacity-70">
                    <Volume2 className="w-4 h-4" />
                    <span>Son d'alerte activé</span>
                </div>
            </div>
        </div>
    );
};

export default AutoClosureNotification;
